/**
 * ReportModal — shared user-facing "Report this content" popup.
 *
 * Wired to POST /community/reports (createReport). Any authenticated user can
 * report a post, a reel, or (in the community feed) a comment. The
 * community-scoped feed has its own inline ReportModal in CommunityFeedPage;
 * this one is for the main feed, post detail, reels, etc. where there is no
 * community context.
 */
import React, { useState } from 'react'
import { Flag, X } from 'lucide-react'
import { createReport } from '@/services/communityApi'

export const REPORT_REASONS = ['SPAM', 'HARASSMENT', 'MISINFORMATION', 'OFF_TOPIC', 'OTHER'] as const

const REASON_LABELS: Record<string, string> = {
  SPAM: 'Spam or misleading',
  HARASSMENT: 'Harassment or abuse',
  MISINFORMATION: 'Misinformation',
  OFF_TOPIC: 'Off-topic / irrelevant',
  OTHER: 'Other',
}

interface ReportModalProps {
  postId?: number
  commentId?: number
  reelId?: number
  onClose: () => void
}

const ReportModal: React.FC<ReportModalProps> = ({ postId, commentId, reelId, onClose }) => {
  const [reason, setReason] = useState<string>('MISINFORMATION')
  const [details, setDetails] = useState('')
  const [saving, setSaving] = useState(false)
  const [done, setDone] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    setError(null)
    try {
      await createReport({
        postId,
        commentId,
        reelId,
        reason,
        details: details || undefined,
      })
      setDone(true)
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Could not submit report. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Report content"
      onClick={onClose}
    >
      <div
        className="absolute inset-0 bg-black/40"
      />
      <div
        className="relative bg-white rounded-xl shadow-xl w-full max-w-md p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Flag className="w-5 h-5 text-red-500" /> Report content
          </h2>
          <button
            onClick={onClose}
            aria-label="Close"
            className="p-2 rounded-lg hover:bg-gray-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {done ? (
          <p className="text-sm text-emerald-600 py-6 text-center">
            Thank you — your report has been submitted and will be reviewed by our team.
          </p>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <p className="text-xs text-gray-500">
              Help us keep the community safe. Reports are reviewed by administrators.
            </p>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
              <select
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:border-blue-500 outline-none"
              >
                {REPORT_REASONS.map((r) => (
                  <option key={r} value={r}>{REASON_LABELS[r]}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Details <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <textarea
                value={details}
                onChange={(e) => setDetails(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:border-blue-500 outline-none"
                placeholder="Add any context that helps us review this…"
              />
            </div>
            {error && <p className="text-sm text-red-600">{error}</p>}
            <div className="flex justify-end gap-2 pt-1">
              <button
                type="button"
                onClick={onClose}
                className="px-3 py-2 text-sm rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-3 py-2 text-sm rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
              >
                {saving ? 'Submitting…' : 'Submit report'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}

export default ReportModal